import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { features } from '@/content/features'
import { cn } from '@/lib/utils'

export default function SiteNav({ variant = 'default' }) {
  const navigate = useNavigate()
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!featuresOpen) return undefined

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setFeaturesOpen(false)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setFeaturesOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [featuresOpen])

  return (
    <nav className="sticky top-0 z-50 border-b bg-white">
      <div className="container mx-auto max-w-7xl px-6">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" onClick={() => setFeaturesOpen(false)}>
            <img src="/wide2.png" alt="Tillio POS" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            {variant === 'onboarding' ? (
              <Button variant="ghost" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            ) : (
              <>
                <div
                  ref={menuRef}
                  className="relative hidden sm:block"
                  onMouseEnter={() => setFeaturesOpen(true)}
                  onMouseLeave={() => setFeaturesOpen(false)}
                >
                  <Button
                    variant="ghost"
                    type="button"
                    aria-expanded={featuresOpen}
                    aria-haspopup="menu"
                    className="gap-1"
                    onClick={() => setFeaturesOpen((open) => !open)}
                  >
                    Features
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        featuresOpen && 'rotate-180'
                      )}
                    />
                  </Button>
                  <div
                    role="menu"
                    className={cn(
                      'absolute left-0 top-full z-50 w-64 pt-2',
                      featuresOpen ? 'block' : 'hidden'
                    )}
                  >
                    <div className="rounded-lg border bg-white p-2 shadow-lg">
                      <Link
                        to="/features"
                        role="menuitem"
                        className="block rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
                        onClick={() => setFeaturesOpen(false)}
                      >
                        All features
                      </Link>
                      <div className="my-1 border-t" />
                      {features.map((feature) => (
                        <Link
                          key={feature.slug}
                          to={feature.path}
                          role="menuitem"
                          className="block rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                          onClick={() => setFeaturesOpen(false)}
                        >
                          {feature.shortTitle || feature.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  className="sm:hidden"
                  onClick={() => navigate('/features')}
                >
                  Features
                </Button>
                <Button variant="ghost" onClick={() => navigate('/pricing')}>
                  Pricing
                </Button>
                <Button
                  variant="ghost"
                  className="hidden sm:inline-flex"
                  onClick={() => navigate('/hardware')}
                >
                  Hardware
                </Button>
                <Button variant="ghost" href="https://app.tilliopayment.com/login">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/onboarding')}>
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
